import { formatUsdtAmount } from '@/utils/format'

/** TRC20 USDT (Tron mainnet) */
export const USDT_TRC20_CONTRACT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'
const TRONGRID_ACCOUNTS_API = 'https://api.trongrid.io/v1/accounts'
const TRONGRID_WALLET_API = 'https://api.trongrid.io/wallet'
const USDT_DECIMALS = 6
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'

/** USDT transfer energy 시뮬레이션용 (TronGrid triggerconstantcontract) */
export const USDT_FEE_SIM_OWNER = 'TQn9Y2khEsLJW1ChVWFMSMeRDow5KcbLSE'
/** USDT를 이미 보유한 주소로 가정 (실제 입금 주소에 가까운 케이스) */
export const USDT_FEE_SIM_RECIPIENT = 'TN3W4H6rK2ce4vX9YnFQHwKENnHjoxb3m9'
export const USDT_TRANSFER_BANDWIDTH_BYTES = 345

export function isValidTronAddress (address) {
  const trimmed = String(address || '').trim()
  return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(trimmed)
}

function decodeBase58 (input) {
  const str = String(input || '')
  if (!str) throw new Error('invalid base58')
  const bytes = [0]
  for (let i = 0; i < str.length; i++) {
    const value = BASE58_ALPHABET.indexOf(str[i])
    if (value < 0) throw new Error('invalid base58')
    for (let j = 0; j < bytes.length; j++) bytes[j] *= 58
    let carry = value
    for (let j = 0; j < bytes.length; j++) {
      bytes[j] += carry
      carry = bytes[j] >> 8
      bytes[j] &= 0xff
    }
    while (carry) {
      bytes.push(carry & 0xff)
      carry >>= 8
    }
  }
  for (let i = 0; i < str.length && str[i] === '1'; i++) bytes.push(0)
  return bytes.reverse()
}

export function tronAddressToAbiHex (address) {
  const bytes = decodeBase58(String(address || '').trim())
  const payload = bytes.slice(0, -4)
  if (payload.length !== 21 || payload[0] !== 0x41) {
    throw new Error('invalid tron address')
  }
  return payload.slice(1).map(byte => byte.toString(16).padStart(2, '0')).join('')
}

export function buildTrc20TransferParameter (toAddress, amountRaw) {
  const toHex = tronAddressToAbiHex(toAddress)
  const amount = Number(amountRaw)
  if (!Number.isFinite(amount) || amount < 0) throw new Error('invalid amount')
  const amountHex = Math.floor(amount).toString(16).padStart(64, '0')
  return `000000000000000000000000${toHex}${amountHex}`
}

/**
 * TronGrid triggerconstantcontract로 USDT transfer energy_used 조회
 * @returns {Promise<number|null>}
 */
export async function estimateUsdtTransferEnergy ({
  ownerAddress = USDT_FEE_SIM_OWNER,
  recipientAddress = USDT_FEE_SIM_RECIPIENT,
  amountRaw = 1000000
} = {}) {
  const parameter = buildTrc20TransferParameter(recipientAddress, amountRaw)
  let res
  try {
    res = await fetch(`${TRONGRID_WALLET_API}/triggerconstantcontract`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        owner_address: ownerAddress,
        contract_address: USDT_TRC20_CONTRACT,
        function_selector: 'transfer(address,uint256)',
        parameter,
        visible: true
      })
    })
  } catch (err) {
    return null
  }

  if (!res.ok) return null
  const body = await res.json()
  if (!body || !body.result || body.result.result !== true) return null
  const energyUsed = Number(body.energy_used)
  if (!Number.isFinite(energyUsed) || energyUsed <= 0) return null
  return energyUsed
}

function parseUsdtFromAccountRow (row) {
  if (!row || !Array.isArray(row.trc20)) return 0
  for (let i = 0; i < row.trc20.length; i++) {
    const entry = row.trc20[i]
    if (!entry) continue
    const raw = entry[USDT_TRC20_CONTRACT]
    if (raw == null || raw === '') continue
    const value = Number(raw)
    if (!Number.isFinite(value)) continue
    return value / Math.pow(10, USDT_DECIMALS)
  }
  return 0
}

/**
 * TronGrid 공용 API로 TRC20 USDT 보유량 조회 (백엔드 미사용)
 * @returns {Promise<number>} USDT 수량
 */
export async function fetchTrc20UsdtBalance (address) {
  const wallet = String(address || '').trim()
  if (!isValidTronAddress(wallet)) {
    throw new Error('올바른 TRC20 지갑 주소를 입력해 주세요.')
  }

  const url = `${TRONGRID_ACCOUNTS_API}/${encodeURIComponent(wallet)}`
  let res
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    })
  } catch (err) {
    throw new Error('지갑 잔액 조회에 실패했습니다. 네트워크 연결을 확인해 주세요.')
  }

  if (!res.ok) {
    throw new Error('지갑 잔액 조회에 실패했습니다.')
  }

  const body = await res.json()
  if (!body || body.success === false) {
    throw new Error('지갑 잔액 조회에 실패했습니다.')
  }

  const row = body.data && body.data.length ? body.data[0] : null
  return parseUsdtFromAccountRow(row)
}

export function formatTrc20UsdtBalance (balance) {
  return formatUsdtAmount(balance, { maxDecimals: 2, minDecimals: 2 })
}
